import { Box, Typography, Card } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PdfViewer } from '../components/PdfViewer';
import { usePayLaterApi } from '../payLaterAPI';
import { RowType } from '../sharedTypes';
import { SummaryList, SidePanelLayout } from '@lendica/components';
import { Bill } from '@lendica/utils';

interface NoContentProps {
    subtitle?: string;
}

interface InvoiceProps {
    invoice: Bill | null;
}

export const NoContent: FC<NoContentProps> = ({ subtitle = '' }) => (
    <Box
        sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        }}
    >
        <Box
            sx={{
                // width: '80%',
                width: 500,
                height: '40%',
                background: `center / 50% no-repeat url("https://lendicapublic.s3.amazonaws.com/assets/illustrations/love-glasses.svg")`,
            }}
        />
        <Typography variant="subtitle1" color="#4A4A68" paragraph>
            {subtitle}
        </Typography>
    </Box>
);

export const InvoiceSummary: FC<InvoiceProps> = ({ invoice }) => {
    return !!invoice ? (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    width: '40%',
                    height: '10%',
                    background: `center / 50% no-repeat url("https://lendicapublic.s3.amazonaws.com/assets/illustrations/love-glasses.svg")`,
                    mb: 2,
                }}
            />
            <Typography variant="subtitle1" color="#4A4A68" paragraph pb={2}>
                Here's a summary of your invoice
            </Typography>
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    py: 2,
                    px: 4,
                    boxSizing: 'border-box',
                }}
            >
                <SummaryList
                    title="Invoice Summary"
                    data={[
                        {
                            label: 'Invoice No.',
                            value: invoice?.invoice_number ?? 'N/A',
                        },
                        {
                            label: 'Vendor Name',
                            value: invoice?.vendor_name ?? 'N/A',
                        },
                        {
                            label: 'Date',
                            value: invoice?.date ?? 'N/A',
                        },
                        {
                            label: 'Due Date',
                            value: invoice?.due_date ?? 'N/A',
                        },
                        {
                            label: 'Subtotal',
                            value: invoice?.subtotal ?? 'N/A',
                        },
                        {
                            label: 'Tax',
                            value: invoice?.tax ?? 'N/A',
                        },
                        {
                            label: 'Total',
                            value: invoice?.total ?? 'N/A',
                        },
                    ]}
                />
            </Card>
        </Box>
    ) : (
        <NoContent subtitle="Invoice file not available.&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;" />
    );
};

export const InvoicePdfViewer: FC<{
    collapsible?: boolean;
}> = ({ collapsible }) => {
    const { billId, type } = useParams<{ billId: string; type: RowType }>();
    const [filename, setFilename] = useState<string | null>(null);
    const [invoice, setInvoice] = useState<Bill | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const api = usePayLaterApi();

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setFilename(null);
                if (type === RowType.Bill) {
                    const data = await api.getBill(billId!);
                    setInvoice(data);
                    setFilename(data.filename);
                } else {
                    setFilename((await api.getDeal(billId!)).filename);
                }
            } catch (e) {
                console.log(e);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 100);
            }
        })();
    }, [billId]);

    return (
        <SidePanelLayout redirectRoute="/paylater" collapsible={collapsible} loading={loading}>
            {filename !== null && filename!.length > 0 ? (
                <PdfViewer invoices={[{ name: '', filename }]} />
            ) : (
                <InvoiceSummary invoice={invoice} />
            )}
        </SidePanelLayout>
    );
};
